// Snapshot -> GameState. Routes through any registered migrations.
// MVP has no prior schema versions yet — the registry is set up so adding one
// later is a single entry, not a refactor.

import type { GameState } from '../state/types.js';
import { CURRENT_SCHEMA, type Snapshot } from './snapshot.js';

export type RestoreError =
  | { code: 'MALFORMED_JSON'; message: string }
  | { code: 'UNKNOWN_SCHEMA'; message: string; foundVersion: number };

type Migration = (s: unknown) => unknown;
const MIGRATIONS: Record<number, Migration> = {
  // Example for the future:
  // 1: (s) => { /* migrate v1 → v2 */ return s; },
};

export function fromJSON(raw: string): { ok: true; snapshot: Snapshot } | { ok: false; error: RestoreError } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: { code: 'MALFORMED_JSON', message: String(e) } };
  }
  return { ok: true, snapshot: parsed as Snapshot };
}

export function restore(snap: Snapshot): { ok: true; state: GameState } | { ok: false; error: RestoreError } {
  let version = snap.schemaVersion as number;
  let state: unknown = snap.state;
  while (version < CURRENT_SCHEMA) {
    const migrate = MIGRATIONS[version];
    if (!migrate) {
      return {
        ok: false,
        error: {
          code: 'UNKNOWN_SCHEMA',
          message: `No migration registered for schema v${version}`,
          foundVersion: version,
        },
      };
    }
    state = migrate(state);
    version += 1;
  }
  // eventsOut is rebuilt by the live engine — restored state starts with an empty queue.
  // Defensive defaults for fields added after a save was written (pre-migration
  // saves predate `investors`); keeps older local saves loadable without a bump.
  const restored = state as GameState;
  return {
    ok: true,
    state: {
      ...restored,
      investors: restored.investors ?? [],
      lastDecisionWeek: restored.lastDecisionWeek ?? 0,
      pendingConsequences: restored.pendingConsequences ?? [],
      landedConsequences: restored.landedConsequences ?? [],
      inSlump: restored.inSlump ?? false,
      lastResilienceWeek: restored.lastResilienceWeek ?? 0,
      services: restored.services ?? { active: false, hourlyRate: 15, plannedHours: 0 },
      social: restored.social ?? { active: false, followers: 0, postedThisWeek: false },
      gaming: restored.gaming ?? { active: false, skill: 0, weekAction: 'rest' },
      trade: restored.trade ?? { active: false, inventory: [], boughtSlots: [], boughtSlotsWeek: 0 },
      property: restored.property ?? { active: false, portfolio: [], boughtSlots: [], boughtSlotsWeek: 0 },
      broker: restored.broker ?? { active: false, workingSlot: null, workingWeek: 0, dealsClosed: 0 },
      eventsOut: [],
    },
  };
}
